import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRTLocale } from './../../../../i18n/mrt-locale';
import { backYouTheme } from '../../../../theme';
import { createTheme, ThemeProvider } from '@mui/material';

export default function StatsTable({
    id = null,
    columns = [],
    data = [],
    isLoaded = false,
	tableLoader = false,
    density = 'compact',
    striped = false,
    initialMRTState = {},
    tableContainerSxProps = {},
    rowsPerPage = [],
    renderTopToolbarCustomActions = () => false,
	onChangeColumnVisibility = () => false,
	refTable = () => null,
    ...props
}) {
	const rerender = useReducer(() => ({}), {})[1];
	const [mounted, setMounted] = useState(0);
	const [delayedRender, setDelayedRender] = useState(false);
	const [columnVisibility, setColumnVisibility] = useState(initialMRTState.columnVisibility ?? {});

    let muiTableProps = {};
    if (id) {
        muiTableProps.id = id;
    }
    let muiTableBodyProps = {};
    if (striped) {
		muiTableBodyProps.sx = {
			'& tr:nth-of-type(odd) > td': {
				backgroundColor: '#f5f5f536'
			}
        };
    }
    let muiPaginationProps = {};
    if (rowsPerPage.length) {
        muiPaginationProps.rowsPerPageOptions = rowsPerPage;
    }

	const table = useMaterialReactTable({
		...props,
		columns,
		data,
		state: { 
			isLoading: !isLoaded,
			showLoadingOverlay: props.showLoadingOverlay ?? false,
			showProgressBars: tableLoader,
			showSkeletons: props.showSkeletons ?? false,
			columnVisibility
		},
		initialState: { 
			...initialMRTState,
			density: density
		},
		layoutMode: 'grid',
		localization: MRTLocale[ENV.lang],
		displayColumnDefOptions: {
			'mrt-row-expand': { size: 70 }
		},
		muiTableProps,
		muiSkeletonProps: {
			animation: 'pulse',
			height: 20
		},
		muiTablePaperProps: {
			elevation: 0,
			sx: {
				borderRadius: '0.38rem!important',
				overflow: 'hidden',
				'.MuiAlert-message': {
					opacity: delayedRender ? 1 : 0
				}
			}
		},
		muiTableContainerProps: {
			sx: {
				...tableContainerSxProps,
				'::-webkit-scrollbar': {
					'width': '.4rem',
					'height': '.4rem'
				},
				'::-webkit-scrollbar-thumb': {
					'backgroundColor': '#AAAAAA',
					'borderRadius': '50px'
				},
				".MuiButtonBase-root.Mui-disabled": {
					display: 'none'
				},
				'.MuiTableCell-root.MuiTableCell-head': {
					padding: '.4em .3em .4em .5em'
				},
				'.Mui-TableHeadCell-Content': {
					alignItems: 'start'
				},
				'.Mui-TableHeadCell-Content-Labels': {
					marginTop: '2px',
					marginRight: '5px',
					'.MuiBadge-root': {
						marginTop: '3px',
						marginBottom: 'auto'
					}
				}
			},
		},
		muiLinearProgressProps: ({ isTopToolbar }) => ({
			sx: {
				display: isTopToolbar ? 'block' : 'none',
				height: '5px'
			}
		}),
		muiTableBodyProps,
		muiPaginationProps,
		renderTopToolbarCustomActions,
		onColumnVisibilityChange: (updater) => {
			setColumnVisibility((prev) => updater instanceof Function ? updater(prev) : updater);
			queueMicrotask(rerender);
		}
	});

	const tableTheme = useMemo(() =>
		createTheme({
			palette: {
				primary: backYouTheme.palette.primary,
				secondary: backYouTheme.palette.secondary,
				warning: {
					main: '#F0672F',
				},
				info: {
					main: '#D8DFE0'
				}
			},
			typography: backYouTheme.typography
		}),
	[backYouTheme]);

	useEffect(() => {
		refTable({
			getFlatHeaders: table.getFlatHeaders,
			getRowModel: table.getRowModel,  //export all rows as seen on the screen (respects pagination, sorting, filtering, etc.)
			getSelectedRowModel: table.getSelectedRowModel,  //only export selected rows
			getPrePaginationRowModel: table.getPrePaginationRowModel  //export all rows, including from the next page, (still respects filtering and sorting)
		});
	}, []);

	useEffect(() => {
		if (mounted > 0) onChangeColumnVisibility(columnVisibility);
		setMounted(mounted + 1);
	}, [columnVisibility]);

	useEffect(() => {
		if (isLoaded && !delayedRender) {
			setDelayedRender(true);
		}
	}, [isLoaded]);

    return (
        <ThemeProvider theme={tableTheme}>
			<MaterialReactTable table={table} />
		</ThemeProvider>
    );
}